import { useEffect, useRef, useState } from 'react';
import ScratcherOverlay from './core/ScratcherOverlay';
import './core/scratcher-overlay.css';

/**
 * Scratcher — thin adapter that overlays the modern ScratcherOverlay mechanism
 * on top of the microsite's existing dynamic content.
 *
 * This preserves the prop surface of the legacy class component so ContentDetails
 * doesn't have to change:
 *   - overlay         CDN URL of the scratch artwork (mapped to ScratcherOverlay `imageUrl`)
 *   - onUpdateStatus  fired with `1` (view impression) once the prize is revealed,
 *                     exactly as the legacy scratcher did — ContentDetails defers the
 *                     view impression to reveal-time when a scratcher is present.
 *   - onToggleLoading legacy loading hook; the overlay self-preloads, so we clear it
 *                     on mount to keep ContentDetails' loading state consistent.
 *   - children        the revealed content (offer / prize / url / message page).
 *
 * The legacy props height/width/isMobile/windowDimensions are no longer needed —
 * ScratcherOverlay is DPR-aware and self-sizes to this host via ResizeObserver.
 */
const Scratcher = (props) => {
  const { overlay, onUpdateStatus, onToggleLoading, onReveal, compact, children } = props;
  const [showOverlay, setShowOverlay] = useState(true);
  // Drives the reveal moment (content spring-in + a single shine sweep).
  const [revealed, setRevealed] = useState(false);
  // Guard so the deferred view impression fires exactly once, even if the
  // reveal-by-scratch and the "Reveal Offer" button both resolve.
  const completedRef = useRef(false);

  // The overlay preloads its own artwork; clear the legacy loading flag on mount.
  useEffect(() => {
    onToggleLoading?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScratchComplete = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    // Record the deferred "view" impression now that the prize is revealed.
    onUpdateStatus?.(1);
    // The overlay has already faded out before onComplete fires; unmount it so
    // the revealed content underneath becomes fully interactive.
    setShowOverlay(false);
    // Trigger the celebration on the now-visible content.
    setRevealed(true);
    // Tell the host (ContentDetails) to pop + expand the card to full content.
    onReveal?.();
  };

  // Host sizing:
  //  - compact scratch card (pre-reveal): fill the card's fixed height exactly
  //    (`height: 100%` of the definite-height ScratchCard) and clip the longer
  //    content, so the scratch canvas matches the visible card and the artwork
  //    covers it cleanly (no over-cropped object-fit). On reveal we release the
  //    height so the in-flow content defines it and the card can expand.
  //  - everything else: `minHeight: 100%` so the overlay fills the whole card
  //    even when the revealed content is shorter (a fixed `.Actions` bar can't
  //    peek below); tall content still grows the host so the overlay covers all.
  const hostStyle = { position: 'relative', width: '100%' };
  if (compact) {
    if (!revealed) { hostStyle.height = '100%'; hostStyle.overflow = 'hidden'; }
  } else {
    hostStyle.minHeight = '100%';
  }

  return (
    <div style={hostStyle}>
      {/* Revealed content stays in normal flow so it defines the height
          (the overlay is absolutely positioned and fills this wrapper).
          On reveal it springs in — but the spring transform is scoped to card
          mode in CSS, since a transform here would establish a containing block
          for the `position: fixed` `.Actions` bar in the non-card layout. */}
      <div className={revealed ? 'av8s-reveal-content av8s-reveal-content--in' : 'av8s-reveal-content'}>
        {children}
      </div>

      {/* Reveal flourish — a single shine sweep over the content (no confetti).
          pointer-events:none so it never blocks the revealed buttons; the motion
          is gated by prefers-reduced-motion. */}
      {revealed && (
        <div className="av8s-burst" aria-hidden="true">
          <span className="av8s-burst-shine" />
        </div>
      )}

      {/* Modern scratch surface on top, removed once revealed. */}
      {showOverlay && (
        <ScratcherOverlay imageUrl={overlay} onComplete={handleScratchComplete} />
      )}
    </div>
  );
};

export default Scratcher;
